import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Dropdown,
  Flex,
  Input,
  Spin,
  Space,
  Table,
  Tabs,
  Tooltip,
  Typography,
} from 'antd';
import {
  AppstoreOutlined,
  DownOutlined,
  FilterOutlined,
  EyeOutlined,
  FileTextOutlined,
  MailOutlined,
  PhoneOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import CustomPagination from './CustomPagination';
import frameIcon from './images/common/frame.svg';
import unorderedListOutlinedIcon from './images/common/unorderedlistoutlined.svg';
import { useLocation, useNavigate } from 'react-router-dom';

const { Text } = Typography;

function getFieldLabel(field) {
  return field.label ?? field.fieldLabel ?? field.name ?? field.fieldName ?? field.value;
}

function getFieldValue(field) {
  return field.value ?? field.fieldName ?? field.name ?? field.label;
}

function getRecordValue(record, fieldKey) {
  const value = record?.[fieldKey];

  if (value === null || value === undefined) return '-';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return Object.values(value).join(' ');

  return value;
}

function tabLabel(label, count) {
  return (
    <Space size={6}>
      <span>{label}</span>
      <Badge count={count} overflowCount={9999} />
    </Space>
  );
}

export default function ParamListView({
  listName = 'List',
  fields = [],
  actions = [],
  dataSource = [],
  loading = false,
  total,
  current,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSearch,
  rowSelection = true,
  className = '',
  tableClassName = 'job-list-table',
  activeView = 'list',
  onViewChange,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [visibleColumnKeys, setVisibleColumnKeys] = useState([]);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const searchTimerRef = useRef(null);

  const menuItems = actions
    .filter((action) => !action.maxSelect || selectedRowKeys.length <= action.maxSelect)
    .map((action) => ({
      key: action.key ?? action.name,
      label: action.label ?? action.name,
      danger: action.danger ?? false,
      disabled: selectedRowKeys.length === 0,
      onClick: () => {
        if (selectedRowKeys.length === 0) return;
        // onSelect actions call straight through (e.g. Shortlist) — no modal,
        // no navigation. The handler owns its own loading/success/error feedback.
        if (typeof action.onSelect === 'function') {
          action.onSelect({ selectedIds: selectedRowKeys, selectedRecords: selectedRows });
          return;
        }
        if (!action.route) return;
        navigate(action.route, {
          state: {
            background: location,
            selectedIds: selectedRowKeys,
            selectedRecords: selectedRows,
            ...(action.popupState ?? {}),
          },
        });
      },
    }));
  const isControlledPagination = typeof current === 'number' && typeof pageSize === 'number';
  const activePagination = isControlledPagination ? { current, pageSize } : pagination;

  const normalizedFields = useMemo(() => (
    fields
      .map((field) => ({
        ...field,
        key: getFieldValue(field),
        label: getFieldLabel(field),
      }))
      .filter((field) => field.key && field.label && !field.tableHidden)
  ), [fields]);

  const visibleKeys = visibleColumnKeys.length
    ? visibleColumnKeys
    : normalizedFields.map((field) => field.key);

  const filteredData = useMemo(() => {
    if (onSearch) return dataSource;
    const q = search.toLowerCase();
    if (!q || q.length < 3) return dataSource;

    return dataSource.filter((record) => (
      normalizedFields.some((field) => (
        String(getRecordValue(record, field.key)).toLowerCase().includes(q)
      ))
    ));
  }, [dataSource, normalizedFields, onSearch, search]);

  const displayTotal = typeof total === 'number'
    ? total
    : search.trim() ? filteredData.length : dataSource.length;

  const pagedData = useMemo(() => {
    if (isControlledPagination) return filteredData;

    const start = (activePagination.current - 1) * activePagination.pageSize;
    return filteredData.slice(start, start + activePagination.pageSize);
  }, [activePagination.current, activePagination.pageSize, filteredData, isControlledPagination]);

  const columns = useMemo(() => (
    normalizedFields
      .filter((field) => visibleKeys.includes(field.key))
      .map((field) => ({
        title: field.label,
        dataIndex: field.key,
        key: field.key,
        sorter: field.sorter === false
          ? false
          : field.sorter ?? ((a, b) => String(getRecordValue(a, field.key)).localeCompare(String(getRecordValue(b, field.key)))),
        width: field.width ?? 180,
        fixed: field.fixed,
        ellipsis: field.ellipsis ?? true,
        className: field.className,
        render: (value, record, index) => (
          field.render
            ? field.render(value, record, index)
            : <Text className="job-cell-primary">{getRecordValue(record, field.key)}</Text>
        ),
      }))
  ), [normalizedFields, visibleKeys]);

  const allColumnsVisible = visibleKeys.length === normalizedFields.length;
  const someColumnsVisible = visibleKeys.length > 0 && !allColumnsVisible;

  const toggleColumn = (key, checked) => {
    setVisibleColumnKeys((current) => {
      const currentKeys = current.length ? current : normalizedFields.map((field) => field.key);
      if (checked) return currentKeys.includes(key) ? currentKeys : [...currentKeys, key];
      return currentKeys.filter((columnKey) => columnKey !== key);
    });
  };

  const columnVisibilityContent = (
    <div className="column-visibility-menu" onClick={(event) => event.stopPropagation()}>
      <Checkbox
        checked={allColumnsVisible}
        indeterminate={someColumnsVisible}
        onChange={(event) => {
          setVisibleColumnKeys(event.target.checked ? normalizedFields.map((field) => field.key) : []);
        }}
      >
        Select All
      </Checkbox>
      {normalizedFields.map((field) => (
        <Checkbox
          key={field.key}
          checked={visibleKeys.includes(field.key)}
          onChange={(event) => toggleColumn(field.key, event.target.checked)}
        >
          {field.label}
        </Checkbox>
      ))}
    </div>
  );

  return (
    <div className={`antd dynamic-list-view ${className}`.trim()}>
      <Card>
        <Flex align="center" justify="space-between">
          <Tabs
            activeKey={activeTab}
            items={[{ key: 'all', label: tabLabel(listName, displayTotal) }]}
            onChange={(key) => {
              setActiveTab(key);
              setPagination({ ...pagination, current: 1 });
            }}
          />
          <Flex align="center" gap={8}>
            <Input
              prefix={<SearchOutlined className="job-search-icon" />}
              placeholder="Min 3 Chars to search"
              value={search}
              onChange={(e) => {
                const val = e.target.value;
                setSearch(val);
                if (onSearch) {
                  clearTimeout(searchTimerRef.current);
                  searchTimerRef.current = setTimeout(() => {
                    onSearch(val);
                    onPageChange?.(1);
                  }, 350);
                } else if (isControlledPagination) {
                  onPageChange?.(1);
                } else {
                  setPagination({ ...pagination, current: 1 });
                }
              }}
              allowClear
              className="job-search-input"
            />
            <Tooltip title="Filter">
              <Button className="job-toolbar-icon-button" icon={<FilterOutlined />} />
            </Tooltip>
            <Tooltip title="Card view">
              <Button
                className={`job-toolbar-icon-button param-view-toggle-btn${activeView === 'card' ? ' param-view-toggle-btn--active' : ''}`}
                icon={<AppstoreOutlined />}
                onClick={() => onViewChange?.('card')}
              />
            </Tooltip>
            <Tooltip title="List view">
              <Button
                className={`job-toolbar-icon-button param-view-toggle-btn${activeView === 'list' ? ' param-view-toggle-btn--active' : ''}`}
                icon={<UnorderedListOutlined />}
                onClick={() => onViewChange?.('list')}
              />
            </Tooltip>
          </Flex>
        </Flex>
      </Card>

      <Card>
        <Flex align="center" gap={3}>
          <Dropdown
            open={columnMenuOpen}
            onOpenChange={setColumnMenuOpen}
            trigger={['click']}
            dropdownRender={() => columnVisibilityContent}
            placement="bottomLeft"
            overlayClassName="column-visibility-dropdown"
          >
            <Button
              type="text"
              icon={<img src={unorderedListOutlinedIcon} alt="" className="job-action-list-icon" />}
              size="small"
              className="job-actions-button"
            />
          </Dropdown>
          {selectedRowKeys.length > 0 && (
            <>
              <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                <Button
                  type="text"
                  size="small"
                  className="job-actions-button job-actions-menu-button"
                >
                  <img src={frameIcon} alt="" className="job-actions-frame-icon" />
                  Actions <DownOutlined className="job-actions-caret" />
                </Button>
              </Dropdown>
              <Text className="job-selected-count">
                Selected ({selectedRowKeys.length})
              </Text>
            </>
          )}
        </Flex>
      </Card>

      <Spin spinning={loading}>
        <Table
          rowSelection={rowSelection ? {
            type: 'checkbox',
            selectedRowKeys,
            // onChange: setSelectedRowKeys,
            onChange: (keys, rows) => {
              setSelectedRowKeys(keys);
              setSelectedRows(rows);
            },
          } : undefined}
          columns={columns}
          dataSource={pagedData}
          size="middle"
          scroll={{ x: 'max-content' }}
          showSorterTooltip={false}
          tableLayout="auto"
          pagination={false}
          className={tableClassName}
          rowKey={(record) => record.id ?? record.key}
        />
      </Spin>

      <CustomPagination
        current={activePagination.current}
        pageSize={activePagination.pageSize}
        total={displayTotal}
        onChange={(page) => {
          if (isControlledPagination) onPageChange?.(page);
          else setPagination((currentState) => ({ ...currentState, current: page }));
        }}
        onPageSizeChange={(size) => {
          if (isControlledPagination) onPageSizeChange?.(size);
          else setPagination({ current: 1, pageSize: size });
        }}
      />
    </div>
  );
}
